import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ConsumerPortalApiService } from './consumer-portal-api.service';

/* eslint-disable @typescript-eslint/naming-convention */
/**
 * Note: PascalCase properties match the backend API model
 */
export interface OrganizationCustomSmtp {
    Id: number;
    OrganizationId: number;
    Host: string;
    Port: number;
    User: string;
    Encryption: 'TLS' | 'SSL' | 'NONE';
    FromAddress: string;
    // Searchable compatibility
    id?: string;
    name?: string;
}

export interface SmtpUpsertRequest {
    OrganizationId: number;
    Host: string;
    Port: number;
    User: string;
    Password?: string;
    Encryption: 'TLS' | 'SSL' | 'NONE';
    FromAddress: string;
}
/* eslint-enable @typescript-eslint/naming-convention */

export interface SmtpTestConfig {
    smtpHost: string;
    smtpPort: number;
    smtpUser: string;
    smtpPassword?: string;
    smtpEncryption: string;
    smtpFromAddress: string;
}

export interface SendEmailResponse {
    success: boolean;
    messageId?: string;
    error?: string;
}

@Injectable({
    providedIn: 'root'
})
export class OrganizationSmtpService {
    private readonly api = inject(ConsumerPortalApiService);
    private readonly translate = inject(TranslateService);

    /**
     * Get SMTP configuration for organization
     * Uses endpoint: GET /organization-custom-smtp/:organizationId
     */
    getSmtpConfig(organizationId: string): Observable<OrganizationCustomSmtp | null> {
        return this.api.get<OrganizationCustomSmtp>(`organization-custom-smtp/${organizationId}`).pipe(
            map(result => {
                // Handle array response (multiple records)
                if (Array.isArray(result) && result.length > 0) {
                    return result[0] as OrganizationCustomSmtp;
                }
                // Handle single object response (Records is a single object, not array)
                if (result && !Array.isArray(result) && typeof result === 'object' && 'Id' in result) {
                    return result as unknown as OrganizationCustomSmtp;
                }
                // Empty array or null/undefined means no config exists
                return null;
            }),
            catchError(error => {
                if (error?.status === 404) {
                    return of(null);
                }
                throw error;
            })
        );
    }

    /**
     * Create or update SMTP configuration (Upsert)
     * Uses endpoint: PUT /organization-custom-smtp/:organizationId
     */
    saveSmtpConfig(config: SmtpUpsertRequest): Observable<OrganizationCustomSmtp> {
        return this.api.save<OrganizationCustomSmtp>(`organization-custom-smtp/${config.OrganizationId}`, config as unknown as OrganizationCustomSmtp);
    }

    /**
     * Delete SMTP configuration (disable custom SMTP)
     * Uses endpoint: DELETE /organization-custom-smtp/:organizationId
     */
    deleteSmtpConfig(organizationId: string): Observable<{ deleted: boolean }> {
        return this.api.delete<{ deleted: boolean }>(`organization-custom-smtp/${organizationId}`);
    }

    /**
     * Send test email using SMTP configuration
     * Uses endpoint: POST /organization-custom-smtp/:organizationId/send-email
     * testMode: true ensures no Mailgun fallback - SMTP errors will be reported accurately
     * smtpConfig: optional override to test current form values instead of saved DB config
     */
    sendTestEmail(organizationId: string, recipientEmail: string, storeName: string, smtpConfig?: SmtpTestConfig): Observable<SendEmailResponse> {
        const subject = this.translate.instant('TestEmailSubject', { storeName });
        const paragraph1 = this.translate.instant('TestEmailBodyParagraph1');
        const paragraph2 = this.translate.instant('TestEmailBodyParagraph2');
        const body: Record<string, unknown> = {
            to: [recipientEmail],
            subject,
            html: `<p>${paragraph1}</p><p>${paragraph2}</p>`,
            testMode: true
        };

        // Include SMTP config override so the backend tests the current form values
        if (smtpConfig) {
            body['smtpHost'] = smtpConfig.smtpHost;
            body['smtpPort'] = smtpConfig.smtpPort;
            body['smtpUser'] = smtpConfig.smtpUser;
            body['smtpEncryption'] = smtpConfig.smtpEncryption;
            body['smtpFromAddress'] = smtpConfig.smtpFromAddress;
            if (smtpConfig.smtpPassword) {
                body['smtpPassword'] = smtpConfig.smtpPassword;
            }
        }

        return this.api
            .post<SendEmailResponse>({
                controller: `organization-custom-smtp/${organizationId}/send-email`,
                method: 'POST',
                body
            })
            .pipe(
                map(result => {
                    if (Array.isArray(result)) {
                        return { success: true } as SendEmailResponse;
                    }
                    return result as SendEmailResponse;
                })
            );
    }
}
